import React, { useRef } from 'react';
import { Button } from 'antd';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { formatPrice } from '../../../utils/priceFormatter';
import { Link } from 'react-router-dom';
import { PRODUCT_ROUTE } from '../../../routes/constant';

const ProductCarousel = ({ title, products, isDarkMode }) => {
  const scrollRef = useRef(null);

  // A product can exist without a published brand/category relation. Hide
  // only those cards: they cannot produce a valid product route.
  const linkedProducts = Array.isArray(products)
    ? products.filter((product) =>
        product?.slug && product?.brand?.slug && product?.brand?.category?.slug,
      )
    : [];

  if (linkedProducts.length === 0) return null;

  const scroll = (direction) => {
    if (scrollRef.current) {
      const { current } = scrollRef;
      const scrollAmount = direction === 'left' ? -300 : 300;
      current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section>
      {/* Section Title with Dark Mode Support */}
      <div className={`
        px-3 py-2 mb-3 border-b rounded-t-lg
        ${isDarkMode 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
        }
      `}>
        <h2 className={`text-sm font-semibold tracking-wider uppercase ${
          isDarkMode ? 'text-white' : 'text-gray-800'
        }`}>
          {title}
        </h2>
      </div>
      
      <div className="relative px-1 group">
        {/* Left Scroll Button - Dark mode support */}
        <button
          onClick={() => scroll('left')}
          className={`
            absolute left-0 z-10 flex items-center justify-center w-8 h-8 -ml-4 
            transition-all -translate-y-1/2 rounded-full shadow-md opacity-0 top-1/2 
            group-hover:opacity-100 hover:scale-110
            ${isDarkMode 
              ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700' 
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }
          `}
        >
          <FiChevronLeft size={18} />
        </button>
        
        {/* Right Scroll Button - Dark mode support */}
        <button
          onClick={() => scroll('right')}
          className={`
            absolute right-0 z-10 flex items-center justify-center w-8 h-8 -mr-4 
            transition-all -translate-y-1/2 rounded-full shadow-md opacity-0 top-1/2 
            group-hover:opacity-100 hover:scale-110
            ${isDarkMode 
              ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700' 
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }
          `}
        >
          <FiChevronRight size={18} />
        </button>

        {/* Products Scroll Container */}
        <div
          ref={scrollRef}
          className="flex gap-2 pb-2 overflow-x-auto scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {linkedProducts.map((product) => (
            <Link
              key={product.id}
              to={PRODUCT_ROUTE(product.brand.category.slug, product.brand.slug, product.slug)}
              className={`
                flex-none w-[140px] md:w-[160px] rounded-lg overflow-hidden transition-all
                ${isDarkMode
                  ? 'bg-gray-800 hover:bg-gray-750 border border-gray-700'
                  : 'bg-white hover:shadow-md border border-gray-100'
                }
              `}
            >
              {/* Product Image - Fixed height */}
              <div className={`
                h-[120px] md:h-[140px] p-2 flex items-center justify-center
                ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}
              `}>
                <img
                  src={product.image}
                  alt={product.title}
                  className="object-contain max-w-full max-h-full"
                />
              </div>
              
              {/* Product Details - Fixed height layout */}
              <div className="p-2 flex flex-col h-[110px]">
                {/* Title - Fixed height with ellipsis */}
                <div className="h-8 overflow-hidden">
                  <h3 className={`text-xs font-medium line-clamp-2 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-800'
                  }`}>
                    {product.title}
                  </h3>
                </div>
                
                {/* Price - Fixed margin */}
                <div className={`text-xs font-bold mt-1 mb-1.5 ${
                  isDarkMode ? 'text-green-400' : 'text-green-600'
                }`}>
                  {formatPrice(product.price)}
                </div>
                
                {/* Full Width Sell Button - Dark mode support */}
                <Button
                  type="primary"
                  size="small"
                  className={`
                    w-full text-[10px] h-6 rounded-md font-medium mt-auto
                    ${isDarkMode 
                      ? 'bg-green-600 hover:bg-green-700 border-green-600' 
                      : 'bg-green-600 hover:bg-green-700'
                    }
                  `}
                >
                  Sell Now
                </Button>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductCarousel;